// ============================================
// src/app/services/cluster.service.ts
// ============================================
// 🔹 Service Angular — Clusters (3 niveaux)
//    Appelle les endpoints /api/clusters/*

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AuthService } from './auth.service';
import {
  ActivityType,
  ClusterGenerique,
  ClusterMarchand,
  ClusterSession,
  CatalogueProduct,
  CatalogueContextualised,
  CreateMerchantClusterPayload,
  CreateSessionPayload,
  CompleteSessionPayload,
  SessionCatalogueItem,
} from '../models/cluster.model';

@Injectable({ providedIn: 'root' })
export class ClusterService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);

  private readonly baseUrl = '/api/clusters';

  // ─────────────────────────────────────────
  // 🔧 Helpers
  // ─────────────────────────────────────────
  private getAuthHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    });
  }

  // ═══════════════════════════════════════════
  // 🟢 Niveau 1 — ClusterGénérique (lecture seule)
  // ═══════════════════════════════════════════

  /**
   * Récupère tous les clusters génériques (9 activityTypes)
   */
  getAllGenericClusters(): Observable<ClusterGenerique[]> {
    return this.http
      .get<{
        success: boolean;
        data: ClusterGenerique[];
      }>(`${this.baseUrl}/generic`, { headers: this.getAuthHeaders() })
      .pipe(map((res) => res.data));
  }

  /**
   * Récupère un cluster générique par activityType
   */
  getGenericCluster(activityType: ActivityType): Observable<ClusterGenerique> {
    return this.http
      .get<{
        success: boolean;
        data: ClusterGenerique;
      }>(`${this.baseUrl}/generic/${activityType}`, {
        headers: this.getAuthHeaders(),
      })
      .pipe(map((res) => res.data));
  }

  /**
   * Récupère le catalogue d'un cluster générique
   * (liste de CatalogueProduct pour un activityType)
   */
  getGenericCatalogue(
    activityType: ActivityType,
  ): Observable<CatalogueProduct[]> {
    return this.http
      .get<{
        success: boolean;
        data: CatalogueProduct[];
      }>(`${this.baseUrl}/generic/${activityType}/catalogue`, {
        headers: this.getAuthHeaders(),
      })
      .pipe(map((res) => res.data));
  }

  /**
   * Récupère le catalogue universel complet (tous les produits)
   */
  getUniversalCatalogue(): Observable<CatalogueProduct[]> {
    return this.http
      .get<{
        success: boolean;
        data: CatalogueProduct[];
      }>(`${this.baseUrl}/catalogue/universal`, {
        headers: this.getAuthHeaders(),
      })
      .pipe(map((res) => res.data));
  }

  // ═══════════════════════════════════════════
  // 🟡 Niveau 2 — ClusterMarchand
  // ═══════════════════════════════════════════

  /**
   * Crée le ClusterMarchand pour le marchand connecté
   * (appelé typiquement pendant l'onboarding)
   */
  createMerchantCluster(
    payload: CreateMerchantClusterPayload,
  ): Observable<ClusterMarchand> {
    return this.http.post<ClusterMarchand>(
      `${this.baseUrl}/merchant`,
      payload,
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * Récupère le ClusterMarchand du user connecté
   */
  getMyMerchantCluster(): Observable<{
    success: boolean;
    data: ClusterMarchand;
  }> {
    return this.http.get<{ success: boolean; data: ClusterMarchand }>(
      `${this.baseUrl}/merchant/me`,
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * Récupère le ClusterMarchand d'un marchand
   */
  getMerchantCluster(merchantId: string): Observable<ClusterMarchand> {
    return this.http.get<ClusterMarchand>(
      `${this.baseUrl}/merchant/${merchantId}`,
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * Recalcule le ClusterMarchand (après changement de dimensions, geo, etc.)
   */
  recalculateMerchantCluster(merchantId: string): Observable<ClusterMarchand> {
    return this.http.put<ClusterMarchand>(
      `${this.baseUrl}/merchant/${merchantId}/recalculate`,
      {},
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * Récupère le catalogue contextualisé d'un marchand
   */
  getMerchantCatalogue(
    merchantId: string,
  ): Observable<CatalogueContextualised[]> {
    return this.http
      .get<{
        success: boolean;
        data: CatalogueContextualised[];
      }>(`${this.baseUrl}/merchant/${merchantId}/catalogue`, {
        headers: this.getAuthHeaders(),
      })
      .pipe(map((res) => res.data));
  }

  // ═══════════════════════════════════════════
  // 🔴 Niveau 3 — ClusterSession
  // ═══════════════════════════════════════════

  /**
   * Crée une nouvelle session (événement)
   */
  createSession(payload: CreateSessionPayload): Observable<ClusterSession> {
    return this.http.post<ClusterSession>(`${this.baseUrl}/sessions`, payload, {
      headers: this.getAuthHeaders(),
    });
  }

  /**
   * Liste toutes les sessions d'un marchand
   */
  getMerchantSessions(merchantId: string): Observable<ClusterSession[]> {
    return this.http.get<ClusterSession[]>(
      `${this.baseUrl}/sessions/${merchantId}`,
      { headers: this.getAuthHeaders() },
    );
  }

  /**
   * Récupère les suggestions de produits pour une session
   */
  getSessionSuggestions(
    sessionId: string,
  ): Observable<
    { catalogProductId: string; reason: string; confidence: number }[]
  > {
    return this.http
      .get<{
        suggestions: {
          catalogProductId: string;
          reason: string;
          confidence: number;
        }[];
      }>(`${this.baseUrl}/sessions/${sessionId}/suggestions`, {
        headers: this.getAuthHeaders(),
      })
      .pipe(map((res) => res.suggestions));
  }

  /**
   * Complète une session avec les données réelles
   */
  completeSession(
    sessionId: string,
    payload: CompleteSessionPayload,
  ): Observable<ClusterSession> {
    return this.http.put<ClusterSession>(
      `${this.baseUrl}/sessions/${sessionId}/complete`,
      payload,
      { headers: this.getAuthHeaders() },
    );
  }

  // ═══════════════════════════════════════════
  // 🚨 Alertes marchands
  // ═══════════════════════════════════════════

  /**
   * Récupère les alertes actives d'un marchand
   */
  getMerchantAlerts(merchantId: string): Observable<MerchantAlertsResponse> {
    return this.http.get<MerchantAlertsResponse>(
      `${this.baseUrl}/alerts/merchant/${merchantId}`,
      { headers: this.getAuthHeaders() },
    );
  }
}

export interface MerchantAlert {
  type: 'waste' | 'rupture' | 'margin' | 'benchmark';
  severity: 'warning' | 'critical';
  merchantId: string;
  activityType: string;
  value: number;
  threshold?: number;
  message: string;
  recommendation: string;
}

export interface MerchantAlertsResponse {
  merchantId: string;
  alertCount: number;
  critical: number;
  warning: number;
  alerts: MerchantAlert[];
}
